import { useMediaQuery } from '@material-ui/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useQuestionSettingsField from '@v4/hooks/questions/useQuestionSettingsField'
import TYPES from '@v4/constants/quiz'
import QuizQuestionsSelector from '@v4/utils/selectors/QuizQuestionsSelector'
import { isDoubleGrid } from '@v4/components/sections/Question/Question.utils'
import useStyles from './RangeRadioButtons.styled'

export const useRangeRadioButtons = ({
	questionId,
	answers,
	type,
	onClick,
	pending,
	justify,
	useOldCheckboxes,
	shadow,
	mediaPosition,
	showMedia,
}) => {
	const min = useQuestionSettingsField('min')
	const max = useQuestionSettingsField('max')
	const settingsDefaultValue = useQuestionSettingsField('default')
	const defaultValue = Number(_.get(answers, [0, 'answer']) ?? settingsDefaultValue)
	const labelCount = useQuestionSettingsField('label_count')
	const settingsLabels = useQuestionSettingsField('labels')
	const buttonVariant = useQuestionSettingsField('button_variant') || 'radio'
	const { updateQuestionSettings } = QuizQuestionsSelector.useActions()

	const labels = useMemo(() => {
		if (typeof labelCount !== 'number') return settingsLabels

		return _.slice(settingsLabels, 0, labelCount)
	}, [labelCount, settingsLabels])

	const range = useMemo(() => {
		if (typeof min !== 'number' && typeof max !== 'number') return []
		if (typeof min === 'number' && typeof max !== 'number') return [{ value: min, key: min, text: min }]
		if (typeof min !== 'number' && typeof max === 'number') return [{ value: max, key: max, text: max }]
		const length = _.clamp(max - min, 0, 11) + 1
		return _.map(Array(length), (value, index) => ({ value: min + index, key: index, text: min + index }))
	}, [max, min])

	const [selectedValue, setSelectedValue] = useState(defaultValue)

	const isMobile = useMediaQuery(theme => theme.breakpoints.down('xs'))

	const labelsLength = _.size(labels)
	const dividedGroups = useMemo(() => {
		if (type === TYPES.RADIO) {
			return []
		}
		const rangeLength = _.size(range)
		const availableGroupCounts = _.reduce(
			range,
			(acc, value, index) => {
				if (!(rangeLength % index)) {
					acc.push(index)
				}
				return acc
			},
			[],
		).reverse()

		availableGroupCounts.unshift(rangeLength)

		const length = _.find(availableGroupCounts, value => value <= labelsLength)

		const itemGroupCount = length && Math.ceil(range.length / length)

		return _.map(Array(length), (value, index) => {
			return {
				key: index,
				items: range.slice(index * itemGroupCount, (index + 1) * itemGroupCount),
			}
		})
	}, [labelsLength, range, type])

	const hasDoubleGrid = showMedia && isDoubleGrid(mediaPosition)

	const classes = useStyles({
		justify,
		isDoubleGrid: hasDoubleGrid,
		isDoubleGridTwelveCols: hasDoubleGrid && labelCount === 12,
		labelCount,
		count: _.size(range),
	})

	const handleChange = useCallback(
		event => {
			const { value } = event.currentTarget
			setSelectedValue(parseInt(value, 10))
		},
		[setSelectedValue],
	)

	const handleSubmit = useCallback(() => {
		onClick({
			answer: typeof selectedValue === 'number' ? selectedValue : null,
		})
	}, [selectedValue, onClick])

	const handleKeyUp = useCallback(
		({ key, target: { value } }) => {
			if (key === 'Enter') {
				setSelectedValue(parseInt(value, 10))
			}
		},
		[setSelectedValue],
	)

	useEffect(() => {
		if (defaultValue !== selectedValue) {
			setSelectedValue(_.some(range, { value: defaultValue }) ? defaultValue : null)
		} else if (typeof selectedValue === 'number' && !_.some(range, { value: selectedValue })) {
			setSelectedValue(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultValue, range])

	const handleChangeLabel = useCallback(
		({ index, value }) => {
			const newLabels = _.cloneDeep(settingsLabels || [])
			_.set(newLabels, [index, 'text'], value)

			updateQuestionSettings({
				id: questionId,
				settings: { labels: newLabels },
			})
		},
		[settingsLabels, questionId, updateQuestionSettings],
	)

	useEffect(() => {
		const handleKeyPress = ({ key }) => {
			if (key === 'Enter') {
				handleSubmit()
			}
		}

		document.addEventListener('keyup', handleKeyPress)
		return () => {
			document.removeEventListener('keyup', handleKeyPress)
		}
	}, [handleSubmit])

	return [
		{
			classes,
			type,
			dividedGroups,
			isMobile,
			selectedValue,
			pending,
			useOldCheckboxes,
			justify,
			labels,
			shadow,
			range,
			buttonVariant,
		},
		{ handleChange, handleKeyUp, handleSubmit, handleChangeLabel },
	]
}
